import { Resp } from '@paradigm/api';
import { logException } from '@paradigm/logging';
import { useDrfqService } from '@paradigm/features/src/unified-rfqs/contexts/drfqs';
import { useGrfqService } from '@paradigm/features/src/unified-rfqs/contexts/grfqs';
import { CancelQuotesBatchResp } from '@paradigm/features/src/unified-rfqs/repositories/grfq-types';
import { CancelAllOrdersResp } from '@paradigm/features/src/unified-rfqs/repositories/drfq-types';
import { toast } from '#/components/UnifiedRfqs/toast';
import { useOBV1APIFeatureFlag } from '@paradigm/features/src/feature-flags/context';
import { RequestError } from '@paradigm/logging/src/errors';
import LoadingSpinner from '@paradigm/design-system/src/components/LoadingSpinner';
import { paradigmLightBlue } from '@paradigm/design-system/src/colors';

const TOAST_AUTO_CLOSE_MS = 1500;
export function useBatchKill() {
  const drfqService = useDrfqService();
  const grfqService = useGrfqService();
  const isGrfqV2APIFeatureFlagEnabled = useOBV1APIFeatureFlag();

  const batchKill = (side: Side) => {
    if (isGrfqV2APIFeatureFlagEnabled) {
      toastBatchCancel(side);
    }
    Promise.all([
      grfqService.cancelQuotesBatch({ side }),
      drfqService.cancelOrdersBatch({ side }),
    ])
      .then(([respGrfq, respDrfq]) => {
        // For APIv2 we'll notify these cancellations through websocket.
        if (!isGrfqV2APIFeatureFlagEnabled) {
          toast(
            messageFromResp(
              respGrfq as unknown as Resp<CancelQuotesBatchResp>,
              respDrfq,
              side,
            ),
          );
        }
      })
      .catch((error: Error) => {
        logException(
          `Unexpected error batch killing quotes (${side})`,
          new RequestError(error.message),
        );
        toast(messageFromError(error.message, side));
      });
  };
  return batchKill;
}

type Side = 'BUY' | 'SELL' | null;

function getSideLabel(side: Side) {
  const kind = side == null ? '' : side === 'BUY' ? 'bid' : 'ask';
  return kind;
}

const messageFromResp = (
  respGrfq: Resp<CancelQuotesBatchResp>,
  respDrfq: Resp<CancelAllOrdersResp>,
  side: Side,
) => {
  if (!respGrfq.ok) return messageFromError(respGrfq.message, side);
  if (!respDrfq.ok) return messageFromError(respDrfq.message, side);

  const successes =
    respGrfq.data.successes.count + respDrfq.data.successes.count;
  const failures = respGrfq.data.failures.count + respDrfq.data.failures.count;
  const quoteSuffix = successes === 1 ? '' : 's';

  return (
    `${successes} ${getSideLabel(side)} quote${quoteSuffix} successfully ` +
    `canceled, ${failures} failed`
  );
};

const messageFromError = (errorMessage: string, side: Side) =>
  `Failed killing ${getSideLabel(side)} quotes: ${errorMessage}`;

const toastBatchCancel = (side: Side) => {
  toast(
    `Killing ${
      side == null ? 'All' : side === 'BUY' ? 'Desk Bid' : 'Desk Ask'
    } Orders...`,
    {
      icon: <LoadingSpinner />,
      styles: {
        borderColor: paradigmLightBlue,
      },
      autoClose: TOAST_AUTO_CLOSE_MS,
    },
  );
};
